<template>
  <label>Тип проекции для топ видов</label>
  <a-select
    :value="projectionMode"
    :style="{ width: '100%', marginBottom: '1rem' }"
    placeholder="Тип проекции для топ видов"
    :options="TopViewsProjectionModeOptions"
    @change="updateProjectionMode" />

  <label>Тип навигации камеры</label>
  <a-select
    v-if="isCameraNavigationSelectVisible"
    :value="cameraNavigation"
    :style="{ width: '100%', marginBottom: '1rem' }"
    placeholder="Тип навигации"
    :options="filteredCameraNavigationOptions"
    @change="updateCameraNavigation" />

  <label v-if="isUnrealTopDownViewCountVisible">
    Количество фотографий вокруг строения
    <a-select
      placeholder="Количество фотографий вокруг строения"
      :value="unrealTopDownViewCount"
      :style="{ width: '100%', marginBottom: '1rem' }"
      @change="updateUnrealTopDownViewCount">
      <a-select-option
        v-for="count in availableUnrealTopDownViewCounts"
        :key="count"
        :value="count">
        {{ count }}
      </a-select-option>
    </a-select>
  </label>

  <label v-if="isPngLosslessFormatChoiceVisible">
    Формат изображения глубины
    <a-select
      :value="losslessFormat"
      :style="{ width: '100%', marginBottom: '1rem' }"
      placeholder="Формат изображения глубины"
      :options="LosslessFormatOptions"
      @change="updateLosslessFormat" />
  </label>

  <template v-if="userHasPermissionForNewOptions">
    <label v-if="isPanoramasTypeChoiceVisible">
      Тип панорамы
      <a-select
        :value="panoramasType"
        :style="{ width: '100%', marginBottom: '1rem' }"
        placeholder="Тип панорамы"
        :options="PanoramasTypeOptions"
        @change="updatePanoramasType" />
    </label>

    <label v-if="isMiddleCutHeightChoiceVisible">
      Высота срединного среза
      <a-select
        placeholder="Высота срединного среза"
        :value="unrealMiddleCutHeight"
        :style="{ width: '100%', marginBottom: '1rem' }"
        @change="updateUnrealMiddleCutHeight">
        <a-select-option
          v-for="count in Array.from({ length: 280 }, (_, i) => i + 1)"
          :key="count"
          :value="count">
          {{ count }}
        </a-select-option>
      </a-select>
    </label>

    <label v-if="isCameraIsoChoiceVisible">
      ISO камеры
      <a-select
        placeholder="ISO камеры"
        :value="cameraIso"
        :style="{ width: '100%', marginBottom: '1rem' }"
        @change="updateCameraIso">
        <a-select-option
          v-for="count in [1, ...Array.from({ length: 120 }, (_, i) => (i + 1) * 10)]"
          :key="count"
          :value="count">
          {{ count }}
        </a-select-option>
      </a-select>
    </label>
  </template>
  <template v-if="userIsAdmin">
    <label>
      Желательный сервер для рендера
      <a-select
        :value="renderServer"
        :style="{ width: '100%', marginBottom: '1rem' }"
        placeholder=""
        :options="renderServersOptions"
        @change="updateRenderServer" />
    </label>
  </template>
</template>

<script setup>
import { TopViewsProjectionModeOptions } from '@/enums/TopViewsProjectionMode';
import { LosslessFormatOptions } from '@/enums/losslessFormatOptions';
import { PanoramasTypeOptions } from '@/enums/PanoramasType';
import { ensureRenderServersLoaded, renderServersOptions } from '@/enums/serversOptions';

ensureRenderServersLoaded();

const availableUnrealTopDownViewCounts = [
  1, 2, 3, 4, 5, 6, 8, 9, 10, 12, 15, 18, 20, 24, 30, 36, 40, 45, 60, 72, 90, 120,
];

defineProps({
  userHasPermissionForNewOptions: {
    type: Boolean,
    default: () => false,
  },
  userIsAdmin: {
    type: Boolean,
    default: () => false,
  },
  projectionMode: {
    type: String,
    required: true,
  },
  cameraNavigation: {
    type: String,
    required: true,
  },
  unrealTopDownViewCount: {
    type: Number,
    default: () => null,
  },
  losslessFormat: {
    type: String,
    default: () => null,
  },
  panoramasType: {
    type: String,
    default: () => null,
  },
  unrealMiddleCutHeight: {
    type: Number,
    default: () => null,
  },
  cameraIso: {
    type: Number,
    default: () => null,
  },
  renderServer: {
    type: String,
    default: () => null,
  },
  filteredCameraNavigationOptions: {
    type: Array,
    default: () => [],
  },
  isUnrealTopDownViewCountVisible: {
    type: Boolean,
    default: false,
  },
  isCameraNavigationSelectVisible: {
    type: Boolean,
    default: false,
  },
  isPngLosslessFormatChoiceVisible: {
    type: Boolean,
    default: false,
  },
  isPanoramasTypeChoiceVisible: {
    type: Boolean,
    default: false,
  },
  isMiddleCutHeightChoiceVisible: {
    type: Boolean,
    default: false,
  },
  isCameraIsoChoiceVisible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:projectionMode',
  'update:renderServer',
  'update:cameraNavigation',
  'update:unrealTopDownViewCount',
  'update:losslessFormat',
  'update:panoramasType',
  'update:unrealMiddleCutHeight',
  'update:cameraIso',
]);

const updateProjectionMode = (newValue) => {
  emit('update:projectionMode', newValue);
};

const updateRenderServer = (newValue) => {
  emit('update:renderServer', newValue);
};

const updateCameraNavigation = (newValue) => {
  emit('update:cameraNavigation', newValue);
};

const updateUnrealTopDownViewCount = (newValue) => {
  emit('update:unrealTopDownViewCount', newValue);
};

const updateLosslessFormat = (newValue) => {
  emit('update:losslessFormat', newValue);
};

const updatePanoramasType = (newValue) => {
  emit('update:panoramasType', newValue);
};

const updateUnrealMiddleCutHeight = (newValue) => {
  emit('update:unrealMiddleCutHeight', newValue);
};

const updateCameraIso = (newValue) => {
  emit('update:cameraIso', newValue);
};
</script>
